import {
  Component,
  ElementRef,
  EventEmitter,
  Inject,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { each, find, flatten, includes, map, remove, union, uniqBy } from 'lodash';
import { fromEvent, Observable, Subscription, SubscriptionLike as ISubscription } from 'rxjs';
import { unsubscribeComponent } from '../../../../shared/helpers/unsubscribe-decorator/unsubscribe-decorators.helper';
import { IListSummaryModalEventObj } from '../../../../shared/modals/list-summary/list-summary-modal.component';
import { ListSummaryGroupings } from '../../../../shared/services/list-summary-groupings/list-summary-groupings.service';
import { ObjectCache } from '../../../../shared/services/object-cache/object-cache.service';
import { IListViewData, IListViewSection } from '../../../../shared/typings/interfaces/list-view.interface';
import { UpdateSelectedStudentIds } from '../../../../store';
import { ListDisplay } from '../list-display.component';
import { ListDisplayService } from '../list-display.service';

interface IListViewSectionExt extends IListViewSection {
  sectionIsChecked: boolean;
}

interface ISelectedStudentIdData {
  studentId: string;
  sectionIndex: number;
  rowIndex: number;
}

/* istanbul ignore next */
@Component({
  selector: 'list-display-student',
  template: require('./list-display-student.component.html'),
  styles: [require('./list-display-student.scss')],
})
@unsubscribeComponent
export class ListDisplayStudents extends ListDisplay implements OnInit {
  @Input() graphName: string;
  @Input() batchActionsMode$?;
  @Output() sortData: EventEmitter<object> = new EventEmitter<object>();
  @Output() clearSearch: EventEmitter<object> = new EventEmitter<object>();
  @ViewChild('sectionContainer') sectionContainer: ElementRef;
  @ViewChild('fixedHeader') fixedHeader: ElementRef;

  listedStudents: {
    [studentId: string]: {
      isChecked: boolean;
    };
  };
  tipState: string;
  selectedStudentIds: ISelectedStudentIdData[] = [];
  selectedSections: IListViewSectionExt[] = [];
  batchEditMode: boolean;
  batchActionsModeSubscription: ISubscription;
  fixedHeaderBoundary: number;
  showShadow: boolean = false;
  sectionInFocus;
  sectionInFocusIndex;
  resizeObservable$: Observable<Event>;
  resizeSubscription$: Subscription;
  temp: boolean;
  curht: number;
  showClearSearchBtn: boolean = false;
  listResized: boolean = false;
  groupingNavEventSubscription: Subscription;

  constructor(
    protected listViewService: ListDisplayService,
    protected objectCache: ObjectCache,
    @Inject('$state') protected $state: any,
    @Inject('Rollbar') protected rollbar: any,
    private ngrxStore: Store<any>,
    private listSummaryGroupings: ListSummaryGroupings,
  ) {
    super(listViewService, objectCache, $state, ngrxStore);
  }

  ngOnInit() {
    this.listedStudents = {};
    this.selectedStudentIds = [];
    this.selectedSections = [];

    this.resizeObservable$ = fromEvent(window, 'resize');
    this.resizeSubscription$ = this.resizeObservable$.subscribe(evt => {
      this.resizeListSize();
    });

    this.kickoffGroupingNav();
    // input is optional
    if (this.batchActionsMode$) {
      this.batchActionsModeSubscription = this.batchActionsMode$.subscribe(mode => {
        // triggered when edit mode is toggled. What needs to run when true vs false
        this.batchEditMode = mode;
        this.tipState = this.batchEditMode ? 'no-events' : 'has-events';
        // resetting selections
        this.listedStudents = {};
        this.selectedStudentIds = [];
        this.selectedSections = [];
        this.store.dispatch(new UpdateSelectedStudentIds([]));
        this.updateSingleRowCheckbox(false, []);
        if (this.formattedList) {
          each(this.formattedList.sections, (sectionInView: IListViewSectionExt) => {
            sectionInView.sectionIsChecked = false;
          });
        }
      });
    }
  }

  ngAfterViewChecked() {
    if (!this.listResized && this.fixedHeader) {
      const fixedHeaderEle = this.fixedHeader.nativeElement.getBoundingClientRect();
      this.fixedHeaderBoundary = fixedHeaderEle.top + fixedHeaderEle.height;
      this.resizeListSize();
      this.listResized = true;
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    super.ngOnChanges(changes);

    if (changes.searchQuery && changes.searchQuery.currentValue) {
      this.showClearSearchBtn = true;
    } else if (changes.searchQuery && !changes.searchQuery.currentValue) {
      this.showClearSearchBtn = false;
    }

    if (this.sectionContainer) {
      // move the scroll bar to the top
      this.sectionContainer.nativeElement.scrollTop = 0;
    }
  }

  clearListSearch() {
    this.clearSearch.emit();
    this.showClearSearchBtn = false;
    this.sectionContainer.nativeElement.scrollTop = 0;
  }

  /* This hack is to fix the following problem:
    To make the list section scrollable with overflow-y auto, the section should have a fixed height.
    But it's not possible to define a fixed height for a dynamic content. So the height has to be
    calculated after the DOM is rendered. Ideal solution would be to structure css in a way
    the height changes with the change in list content (eg: when you search) (DVN)
  */
  resizeListSize() {
    const madLibEle = document.getElementsByClassName('madlib-list-wrap');
    const listStickyHeaderEle = document.getElementsByClassName('first-header');
    const listEle = document.getElementsByClassName('list-section-wrap');

    const windowHeight = window.innerHeight;
    const madlibHeight = (madLibEle && madLibEle[0].getBoundingClientRect().height) || 0;
    const listHeaderHeight = (listStickyHeaderEle.length && listStickyHeaderEle[0].getBoundingClientRect().height) || 0;

    let idealListHeight = windowHeight - (madlibHeight + listHeaderHeight);
    if (listEle && listEle.length) {
      listEle[0].setAttribute('style', 'max-height:' + idealListHeight + 'px');
    }
  }

  formatData(parsedData: IListViewData): void {
    super.formatData(parsedData);
    this.formattedList.sections = map(this.formattedList.sections, (section: IListViewSection) => {
      section[`sectionIsChecked`] = false;
      return section;
    });
    const allStudentRows = this.getStudentsFromSections(this.formattedList.sections);
    this.setInitialRowStatus(allStudentRows);
    if (this.formattedList.sections.length > 0) {
      this.sectionInFocus = this.formattedList.sections[0];
      this.sectionInFocusIndex = 0;
    }
  }

  rowClick(dataRow, sectionIndex: number, rowIndex: number): void {
    const studentId = dataRow[this.navDataIndex].dependencies.studentId;
    if (this.batchEditMode) {
      // prevents row from being selected if user is in batch edit mode
      this.sendStudentToBatchEdit(studentId, sectionIndex, rowIndex);
      return;
    }
    const cachedStudentId = this.objectCache.cacheObject({ studentId: [studentId] });
    this.$state.go('main.school.single-student-filter-all', { filter: cachedStudentId });
  }

  updateSort(key, dataType, defaultOrder, currentOrder) {
    if (this.batchEditMode) return;
    super.updateSort(key, dataType, defaultOrder, currentOrder);
  }

  sendSectionToBatchEdit(currentSection: IListViewSectionExt, sectionIndex: number): void {
    const sectionExists = find(this.selectedSections, (selectedSelection: IListViewSectionExt) => {
      return selectedSelection.name === currentSection.name;
    });

    const idsExist = this.selectedStudentIds && this.selectedStudentIds.length;

    if (!sectionExists) {
      currentSection.sectionIsChecked = true;
      this.selectedSections.push(currentSection);
      const selectedStudents = this.getStudentsFromSections([currentSection]);
      const idsAndListPosition = this.getStudentIdsAndPosition(selectedStudents, sectionIndex);
      const selectedStudentIdData = idsExist ? this.selectedStudentIds.concat(idsAndListPosition) : idsAndListPosition;
      this.selectedStudentIds = uniqBy(selectedStudentIdData, 'studentId');
      this.updateSingleRowCheckbox(true, this.selectedStudentIds);
      this.updateSelectAllCheckbox(currentSection, true);
      this.sendStudentsToEdit(this.selectedStudentIds);
    } else {
      currentSection.sectionIsChecked = false;
      const selectedStudents = this.getStudentsFromSections([currentSection]);
      const idsAndListPosition = this.getStudentIdsAndPosition(selectedStudents, sectionIndex);
      const idsToRemove = idsAndListPosition.map(studentObj => studentObj.studentId);

      this.selectedStudentIds = idsExist
        ? remove(this.selectedStudentIds, student => !includes(idsToRemove, student.studentId))
        : idsAndListPosition;

      this.removeFromSelectedSections(currentSection);
      this.updateSingleRowCheckbox(false, idsAndListPosition);
      this.updateSelectAllCheckbox(currentSection, false);
      this.sendStudentsToEdit(this.selectedStudentIds);
    }
  }

  private setInitialRowStatus(students): void {
    if (!this.batchEditMode) {
      this.listedStudents = {};
      each(students, (student: any) => {
        this.listedStudents[student.studentName.dependencies.studentId] = { isChecked: false };
      });
    }
  }

  private sendStudentToBatchEdit(studentId: string, sectionIndex: number, rowIndex: number): void {
    const studentListPositionData = { studentId, sectionIndex, rowIndex };

    if (!this.listedStudents[studentId]) {
      this.listedStudents[studentId] = { isChecked: true };
      this.selectedStudentIds.push(studentListPositionData);
    } else {
      // student is already initialized
      const studentIsChecked = this.listedStudents[studentId].isChecked;

      if (!studentIsChecked) {
        this.listedStudents[studentId].isChecked = true;
        this.selectedStudentIds.push(studentListPositionData);
      } else {
        this.listedStudents[studentId].isChecked = false;
        this.selectedStudentIds = remove(this.selectedStudentIds, student => {
          return student.studentId !== studentId;
        });

        // determine whether student was part of a batch selection and update that section's "Select All" checkbox
        if (this.selectedSections.length > 0) {
          each(this.selectedSections, (selectedSection: IListViewSectionExt) => {
            return find(selectedSection.data, (student: any) => {
              if (student.studentName.dependencies.studentId === studentId) {
                selectedSection.sectionIsChecked = false;
                this.removeFromSelectedSections(selectedSection);
                this.updateSelectAllCheckbox(selectedSection, false);
              }
            });
          });
        }
      }
    }

    this.sendStudentsToEdit(this.selectedStudentIds);
  }

  private getStudentsFromSections(sections: IListViewSection[]): any[] {
    return flatten(
      map(sections, (section: IListViewSection) => {
        return section.data;
      }),
    );
  }

  private getStudentIdsAndPosition(
    studentRow: Array<{ studentName: { dependencies: { studentId: string } } }>,
    sectionIndex: number,
  ): ISelectedStudentIdData[] {
    return map(studentRow, (row, i) => {
      return {
        studentId: row.studentName.dependencies.studentId,
        sectionIndex,
        rowIndex: i,
      };
    });
  }

  private removeFromSelectedSections(currentSection: IListViewSection): IListViewSection[] {
    return remove(this.selectedSections, (selectedSection: IListViewSection) => {
      return selectedSection.name === currentSection.name;
    });
  }

  private sendStudentsToEdit(studentIds: ISelectedStudentIdData[]): void {
    this.store.dispatch(new UpdateSelectedStudentIds(union(studentIds)));
  }

  private updateSelectAllCheckbox(currentSection: IListViewSectionExt, isSelected?: boolean) {
    return each(this.formattedList.sections, (sectionInView: IListViewSectionExt) => {
      if (sectionInView.name === currentSection.name) {
        sectionInView.sectionIsChecked = isSelected;
      }
      return sectionInView;
    });
  }

  private updateSingleRowCheckbox(isChecked: boolean, studentIds: ISelectedStudentIdData[]): void {
    each(studentIds, (student: ISelectedStudentIdData) => {
      this.listedStudents[student.studentId] = { isChecked };
    });
  }

  // override method to set graph-specific styles
  setListLevelStyle(parsedData: IListViewData): void {
    if (this.parsedData !== null) this.listStyle = this.listViewService.getStyleForList(parsedData.listType);
  }

  onScroll() {
    if (this.fixedHeader) {
      this.showShadow = super.showPanelShadow(this.sectionContainer.nativeElement.scrollTop);

      const headerRows = this.sectionContainer.nativeElement.getElementsByClassName('header-row');
      const sectionHeaderRowPositions = {};
      const fixedHeaderEle = this.fixedHeader.nativeElement.getBoundingClientRect();
      this.fixedHeaderBoundary = fixedHeaderEle.top + fixedHeaderEle.height;

      [].forEach.call(headerRows, function(row, index: number) {
        sectionHeaderRowPositions[index] = row.getBoundingClientRect().top;
      });
      this.sectionInFocusIndex = 0;
      Object.keys(sectionHeaderRowPositions).forEach(key => {
        const sectionHeaderTopPos = sectionHeaderRowPositions[key];
        if (sectionHeaderTopPos + 65 <= this.fixedHeaderBoundary) {
          this.sectionInFocusIndex = key;
        }
      });

      this.sectionInFocus = this.formattedList.sections[this.sectionInFocusIndex];
    }
  }

  kickoffGroupingNav(): void {
    if (this.listViewService.listShouldHaveGroupingNav(this.parsedData.listType)) {
      this.groupingNavEventSubscription = this.listSummaryGroupings.groupingNavEvent$.subscribe(
        ($event: IListSummaryModalEventObj) => {
          this.scrollToSection($event.sectionIndex);
        },
      );
    }
  }

  scrollToSection(sectionIndex: number): void {
    if (this.sectionContainer) {
      const sectionEl = this.sectionContainer.nativeElement.getElementsByTagName('section')[sectionIndex];
      if (sectionEl) {
        const sectionElRect = sectionEl.getBoundingClientRect();
        const top =
          this.sectionContainer.nativeElement.scrollTop +
          (sectionIndex === 0
            ? sectionElRect.y - this.fixedHeaderBoundary
            : sectionElRect.y - this.fixedHeaderBoundary + 96 + 6);
        this.sectionContainer.nativeElement.scrollTo({ top, behavior: 'smooth' });
      }
    }
  }
}
